/**
 * Constants for the Checkout component and related subcomponents
 */

/** Fallback success message for new quotation (i18n label: checkout.success.create) */
export const SUCCESS_MESSAGE = 'The quotation has been successfully saved to the prospect.';

/** Fallback success message for updated quotation (i18n label: checkout.success.update) */
export const SUCCESS_UPDATE_MESSAGE =
  'The quotation has been successfully updated to the prospect.';

/** Fallback error message for form validation failures (i18n label: checkout.validation.error) */
export const VALIDATION_ERROR = 'Please fix the form errors before submitting.';

/** Partner compensation percentage threshold for discounts (7%) */
export const PARTNER_COMPENSATION_PERCENTAGE = 7;

/** Agency cash discount cap percentage (7%) */
export const AGENCY_CASH_DISCOUNT_CAP = 7;

/** Payment methods that support cash/percentage discount fields */
export const DISCOUNT_ENABLED_PAYMENT_METHODS = ['Cash', '3rd Party Financing'];

/** All available payment method values */
export const PAYMENT_METHOD_VALUES = ['Cash', 'LFS Lease', 'LFS Loan', '3rd Party Financing'];

/** Payment methods that require quotation details (Loan/Leasing) */
export const LOAN_PAYMENT_METHODS = PAYMENT_METHOD_VALUES.filter(
  (method) => !DISCOUNT_ENABLED_PAYMENT_METHODS.includes(method)
);

/** Docusign statuses that indicate the document has been sent */
export const DOCUSIGN_SENT_STATUSES = ['Sent', 'DocuSign Sent', 'Shared'];

/** Fallback title when vehicle is no longer available (i18n label: checkout.vehicle.unavailable.title) */
export const VEHICLE_UNAVAILABLE_TITLE = 'Vehicle Unavailable';

/** Fallback message when vehicle is no longer available (i18n label: checkout.vehicle.unavailable.message) */
export const VEHICLE_UNAVAILABLE_MESSAGE =
  'The vehicle is no longer available. Please find a new vehicle to proceed with quote.';

/** Quote form field names - single source of truth for all field name strings */
export const QUOTE_FORM_FIELDS = {
  // Customer type
  PROSPECT_TYPE: 'prospectType',

  // Payment
  PREFERRED_PAYMENT_METHOD: 'preferredPaymentMethod',
  DELIVERY_TIMELINE: 'deliveryTimeline',

  // Business details
  COMPANY_NAME: 'companyName',
  COMPANY_TAX_ID: 'companyTaxId',
  INVOICE_ADDRESS: 'invoiceAddress',
  BUSINESS_POSTAL_CODE: 'businessPostalCode',
  BUSINESS_CITY: 'businessCity',
  INVOICE_COUNTRY: 'invoiceCountry',

  // Finance company details
  FINANCE_COMPANY_NAME: 'financeCompanyName',
  FINANCE_COMPANY_ADDRESS: 'financeCompanyAddress',
  FINANCE_POSTAL_CODE: 'financePostalCode',
  FINANCE_CITY: 'financeCity',
  FINANCE_COUNTRY: 'financeCountry',
  FINANCE_VAT: 'financeVat',

  // Loan/Leasing fields
  UPLOAD_BANK_QUOTATION: 'uploadBankQuotation',
  QUOTE_ID: 'quoteId',
  AGENT_SUBVENTION_AMOUNT: 'agentSubventionAmount',
  MONTHLY_PAYMENT_INCL_VAT: 'monthlyPaymentInclVat',
  DOWNPAYMENT: 'downpayment',

  // Discount fields
  REQUESTED_INCENTIVES: 'requestedIncentives',
  CASH_DISCOUNT: 'cashDiscount',
  PERCENTAGE_DISCOUNT: 'percentageDiscount',
} as const;

/** Type for quote form field names */
export type QuoteFormFieldName = (typeof QUOTE_FORM_FIELDS)[keyof typeof QUOTE_FORM_FIELDS];
